import { AdditiveBlending, DoubleSide, NormalBlending, ShaderMaterial, Vector2 } from 'three';
import type { MapDef } from '@cloudpearl/config';
import { ATMOSPHERE, LIGHTING, NOISE, WORLD_UNIFORMS } from '../shaders/chunks';
import type { SharedUniforms } from '../shaders/shared';
import { col } from './common';

// ------------------------------------------------------------------------------------------ waterfall

const WATERFALL_VS = /* glsl */ `
${WORLD_UNIFORMS}
varying vec2 vUv;
varying vec3 vWorldPos;
varying float vRand;
void main() {
  mat4 m = modelMatrix;
  #ifdef USE_INSTANCING
    m = modelMatrix * instanceMatrix;
  #endif
  vec4 wPos = m * vec4(position, 1.0);
  vUv = uv;
  vWorldPos = wPos.xyz;
  vRand = fract(sin(dot(m[3].xz, vec2(12.9898, 78.233))) * 43758.5453);
  gl_Position = projectionMatrix * viewMatrix * wPos;
}
`;

const WATERFALL_FS = /* glsl */ `
${WORLD_UNIFORMS}
${NOISE}
${ATMOSPHERE}
uniform vec3 uWater;
varying vec2 vUv;
varying vec3 vWorldPos;
varying float vRand;
vec3 shAmb(vec3 n) {
  float y = n.y;
  return max(uSH[0] * 0.886227 + uSH[1] * 1.023328 * y, vec3(0.0)) / 3.14159265;
}
void main() {
  float u = vUv.x;
  float v = vUv.y;
  float edge = smoothstep(0.0, 0.18, u) * smoothstep(1.0, 0.82, u);
  vec2 q = vec2(u * 7.0 + vRand * 10.0, v * 5.0 - uTime * 2.6);
  float streaks = vnoise(vec2(q.x, q.y * 0.35)) * 0.6 + vnoise(q * vec2(2.3, 0.6) + 3.0) * 0.4;
  float foam = smoothstep(0.35, 0.8, streaks);
  float mist = smoothstep(0.55, 1.0, v);
  vec3 c = mix(uWater * 0.75, vec3(1.0), foam * 0.8 + mist * 0.4);
  c *= uSunColor * 0.16 + shAmb(vec3(0.0, 0.3, 1.0)) * 0.6;
  float alpha = edge * (0.35 + foam * 0.4) * (1.0 - smoothstep(0.75, 1.0, v)) * smoothstep(0.0, 0.03, v);
  gl_FragColor = vec4(applyFog(c, vWorldPos), alpha);
}
`;

export function createWaterfallMaterial(shared: SharedUniforms, map: MapDef): ShaderMaterial {
  return new ShaderMaterial({
    name: 'waterfall',
    uniforms: { ...shared.all(), uWater: col(map.palette.water) },
    vertexShader: WATERFALL_VS,
    fragmentShader: WATERFALL_FS,
    transparent: true,
    depthWrite: false,
    side: DoubleSide,
    blending: NormalBlending,
  });
}

// ------------------------------------------------------------------------------------------ wind gates

const GATE_VS = /* glsl */ `
${WORLD_UNIFORMS}
varying vec2 vUv;
varying vec3 vWorldPos;
varying vec3 vNormal;
void main() {
  mat4 m = modelMatrix;
  #ifdef USE_INSTANCING
    m = modelMatrix * instanceMatrix;
  #endif
  vec3 p = position;
  // A gentle breathing pulse.
  p *= 1.0 + sin(uTime * 2.2 + m[3].x * 0.1) * 0.02;
  vec4 wPos = m * vec4(p, 1.0);
  vUv = uv;
  vWorldPos = wPos.xyz;
  vNormal = normalize(mat3(m) * normal);
  gl_Position = projectionMatrix * viewMatrix * wPos;
}
`;

const GATE_FS = /* glsl */ `
${WORLD_UNIFORMS}
${NOISE}
${ATMOSPHERE}
uniform vec3 uGateColor;
uniform vec3 uGateCore;
varying vec2 vUv;
varying vec3 vWorldPos;
varying vec3 vNormal;
void main() {
  vec3 V = normalize(cameraPosition - vWorldPos);
  float fres = pow(1.0 - abs(dot(normalize(vNormal), V)), 2.0);
  // Wind streaming around the ring.
  float flow = vnoise(vec2(vUv.x * 40.0 - uTime * 6.0, vUv.y * 3.0)) * vnoise(vec2(vUv.x * 13.0 + uTime * 2.0, vUv.y * 6.0 + 4.0));
  float bands = smoothstep(0.25, 0.75, flow);
  vec3 c = mix(uGateColor, uGateCore, bands) * (1.1 + fres * 2.2);
  float dist = length(cameraPosition - vWorldPos);
  // Stay readable from far away (fog only lightly), fade a little when very close.
  vec3 rd;
  float f = fogAmount(vWorldPos, rd) * 0.5;
  c = mix(c, fogColorFor(rd), f);
  float a = (0.45 + bands * 0.55) * (0.6 + fres) * smoothstep(1.0, 6.0, dist);
  gl_FragColor = vec4(c * a, 1.0);
}
`;

export function createGateMaterial(shared: SharedUniforms, map: MapDef): ShaderMaterial {
  return new ShaderMaterial({
    name: 'gate',
    uniforms: { ...shared.all(), uGateColor: col(map.id === 'dunes' ? '#ffb46a' : '#9fd8ff'), uGateCore: col('#ffffff') },
    vertexShader: GATE_VS,
    fragmentShader: GATE_FS,
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    side: DoubleSide,
  });
}

// ------------------------------------------------------------------------------------------ north beacon

const DOOR_VS = /* glsl */ `
varying vec3 vWorldPos;
varying vec3 vNormal;
varying vec3 vLocal;
void main() {
  vec4 wPos = modelMatrix * vec4(position, 1.0);
  vWorldPos = wPos.xyz;
  vNormal = normalize(mat3(modelMatrix) * normal);
  vLocal = position;
  gl_Position = projectionMatrix * viewMatrix * wPos;
}
`;

const DOOR_FS = /* glsl */ `
${WORLD_UNIFORMS}
${NOISE}
${ATMOSPHERE}
${LIGHTING}
uniform vec3 uRock;
uniform vec3 uLight;
uniform vec2 uOpen;   // opening width, height
uniform float uHaze;
varying vec3 vWorldPos;
varying vec3 vNormal;
varying vec3 vLocal;
void main() {
  vec3 n = normalize(vNormal);
  float h01 = clamp(vLocal.y / (uOpen.y * 1.15), 0.0, 1.0);
  float ndl = dot(n, uSunDir);
  vec3 lit = uRock * (hemiAmbient(n) * 0.85 + uSunColor * 0.3 * smoothstep(-0.25, 0.9, ndl));
  vec3 V = normalize(cameraPosition - vWorldPos);
  lit += uSunColor * uRock * pow(1.0 - abs(dot(n, V)), 3.0) * 0.2 * smoothstep(-0.2, 0.6, ndl);
  lit *= 0.9 + vnoise(vLocal.xy * 0.03) * 0.2;
  // Light from the opening washes the faces turned toward it: the jambs, the lintel's
  // underside, the threshold, and the front faces right around the edge of the opening.
  float hw = uOpen.x * 0.5;
  float ax = abs(vLocal.x);
  float inOpening = step(vLocal.y, uOpen.y) * step(-0.5, vLocal.y);
  float jamb = smoothstep(hw * 1.6, hw, ax) * clamp(-sign(vLocal.x) * n.x, 0.0, 1.0) * inOpening;
  float under = clamp(-n.y, 0.0, 1.0) * step(ax, hw * 1.2) * step(uOpen.y * 0.9, vLocal.y);
  float sill = clamp(n.y, 0.0, 1.0) * step(ax, hw * 1.4) * step(vLocal.y, uOpen.y * 0.05);
  float rim = clamp(n.z, 0.0, 1.0) * exp(-max(ax - hw, 0.0) / (hw * 0.18)) * exp(-max(vLocal.y - uOpen.y, 0.0) / (uOpen.y * 0.05)) * inOpening;
  float glow = jamb * 1.1 + under * 0.8 + sill * 0.9 + rim * 0.6;
  // Far away and wrapped in haze, densest at its feet; its own light cuts through a little.
  vec3 rd = -V;
  float haze = mix(0.9, uHaze, smoothstep(0.0, 0.5, h01));
  vec3 c = mix(lit, fogColorFor(rd), haze);
  c += uLight * glow * (1.0 - haze * 0.55) * 1.4;
  gl_FragColor = vec4(c, 1.0);
}
`;

const DOOR_LIGHT_VS = /* glsl */ `
varying vec2 vUv;
varying vec3 vWorldPos;
void main() {
  vUv = uv;
  vec4 wPos = modelMatrix * vec4(position, 1.0);
  vWorldPos = wPos.xyz;
  gl_Position = projectionMatrix * viewMatrix * wPos;
}
`;

const DOOR_LIGHT_FS = /* glsl */ `
${WORLD_UNIFORMS}
${NOISE}
${ATMOSPHERE}
uniform vec3 uLight;
varying vec2 vUv;
varying vec3 vWorldPos;
void main() {
  // Whatever is beyond the door is brighter than the day: a hot core low in the opening, soft
  // vertical rays drifting across it, a little haze in front.
  vec2 c = (vUv - vec2(0.5, 0.32)) * vec2(2.4, 1.3);
  float core = exp(-dot(c, c) * 2.4);
  float rays = 0.8 + 0.2 * vnoise(vec2(vUv.x * 16.0, uTime * 0.12)) + 0.08 * vnoise(vec2(vUv.x * 41.0 - uTime * 0.2, 3.0));
  vec3 col = uLight * (1.5 + core * 3.4) * rays;
  vec3 rd = normalize(vWorldPos - cameraPosition);
  gl_FragColor = vec4(mix(col, fogColorFor(rd) * 1.4, 0.18), 1.0);
}
`;

const SPILL_VS = /* glsl */ `
attribute float aAlong;
attribute float aAcross;
varying float vAlong;
varying float vAcross;
varying vec3 vWorldPos;
void main() {
  vAlong = aAlong;
  vAcross = aAcross;
  vec4 wPos = modelMatrix * vec4(position, 1.0);
  vWorldPos = wPos.xyz;
  gl_Position = projectionMatrix * viewMatrix * wPos;
}
`;

const SPILL_FS = /* glsl */ `
${WORLD_UNIFORMS}
${NOISE}
uniform vec3 uLight;
varying float vAlong;
varying float vAcross;
varying vec3 vWorldPos;
void main() {
  // Soft rays of light fanning out of the opening, fading toward their tips and breathing slowly.
  // (Clamped: interpolation can overshoot 0..1 by a hair, and pow() of a negative is NaN —
  // which bloom would smear into visible specks.)
  float edge = pow(max(sin(clamp(vAcross, 0.0, 1.0) * 3.14159), 0.0), 2.0);
  float fall = pow(clamp(1.0 - vAlong, 0.0, 1.0), 1.8);
  float breathe = 0.7 + 0.3 * vnoise(vec2(vAcross * 3.0 + vWorldPos.x * 0.002, uTime * 0.08));
  gl_FragColor = vec4(uLight * edge * fall * breathe * 0.16, 1.0);
}
`;

const BEAM_VS = /* glsl */ `
varying vec3 vWorldPos;
varying vec3 vNormal;
varying float vT;
void main() {
  vec4 wPos = modelMatrix * vec4(position, 1.0);
  vWorldPos = wPos.xyz;
  vNormal = normalize(mat3(modelMatrix) * normal);
  vT = position.y;
  gl_Position = projectionMatrix * viewMatrix * wPos;
}
`;

const BEAM_FS = /* glsl */ `
${WORLD_UNIFORMS}
${NOISE}
uniform vec3 uBeam;
uniform float uOpacity;
uniform float uRoot;
varying vec3 vWorldPos;
varying vec3 vNormal;
varying float vT;
void main() {
  vec3 V = normalize(cameraPosition - vWorldPos);
  float core = pow(abs(dot(normalize(vNormal), V)), 2.5);
  float fade = smoothstep(0.0, uRoot, vT) * pow(clamp(1.0 - vT, 0.0, 1.0), 1.6);
  float shimmer = 0.85 + 0.15 * vnoise(vec2(vT * 40.0 - uTime * 3.0, 0.5));
  gl_FragColor = vec4(uBeam * core * fade * shimmer * 2.6 * uOpacity, 1.0);
}
`;

const PEAK_VS = /* glsl */ `
attribute float aU;
varying vec3 vWorldPos;
varying vec3 vNormal;
varying float vU;
void main() {
  vec4 wPos = modelMatrix * vec4(position, 1.0);
  vWorldPos = wPos.xyz;
  vNormal = normalize(mat3(modelMatrix) * normal);
  vU = aU;
  gl_Position = projectionMatrix * viewMatrix * wPos;
}
`;

const PEAK_FS = /* glsl */ `
${WORLD_UNIFORMS}
${NOISE}
${ATMOSPHERE}
${LIGHTING}
uniform vec3 uRock;
uniform vec3 uLight;
uniform float uHaze;
varying vec3 vWorldPos;
varying vec3 vNormal;
varying float vU;
void main() {
  vec3 n = normalize(vNormal);
  vec3 V = normalize(cameraPosition - vWorldPos);
  // Sunlit from above; the hanging flanks fall into soft, sky-filled shade.
  float ndl = dot(n, uSunDir);
  vec3 lit = uRock * (hemiAmbient(n) * 0.85 + uSunColor * 0.32 * smoothstep(-0.25, 0.9, ndl));
  lit += uSunColor * uRock * pow(1.0 - abs(dot(n, V)), 3.0) * 0.22 * smoothstep(-0.2, 0.6, ndl);
  lit *= 0.88 + vnoise(vWorldPos.xz * 0.012 + vWorldPos.y * 0.008) * 0.24;
  // The crown catches the sky like a meadow seen from far below.
  lit = mix(lit, lit * 1.25 + uSunGlow * 0.05, smoothstep(0.35, 0.9, n.y) * step(0.999, vU));
  // Far away and aloft: deep in the haze, a touch less toward the sunlit crown.
  vec3 c = mix(lit, fogColorFor(-V), mix(uHaze * 1.05, uHaze * 0.9, vU));
  // Its own light: the tip glows, and the glow creeps up the flanks.
  c += uLight * (exp(-vU * 10.0) * 1.8 + exp(-vU * 3.5) * 0.18);
  gl_FragColor = vec4(c, 1.0);
}
`;

const FALL_VS = /* glsl */ `
varying vec3 vWorldPos;
varying vec3 vNormal;
varying float vT;
varying float vAng;
void main() {
  vec4 wPos = modelMatrix * vec4(position, 1.0);
  vWorldPos = wPos.xyz;
  vNormal = normalize(mat3(modelMatrix) * normal);
  vT = -position.y;
  vAng = atan(position.z, position.x);
  gl_Position = projectionMatrix * viewMatrix * wPos;
}
`;

const FALL_FS = /* glsl */ `
${WORLD_UNIFORMS}
${NOISE}
uniform vec3 uLight;
uniform float uGain;
uniform float uSharp;
varying vec3 vWorldPos;
varying vec3 vNormal;
varying float vT;
varying float vAng;
void main() {
  // A beam of light straight down from the tip: steady and bright all the way to the ground (it
  // points at a place), with motes of light drifting down through it.
  vec3 V = normalize(cameraPosition - vWorldPos);
  float core = pow(clamp(abs(dot(normalize(vNormal), V)), 0.0, 1.0), uSharp);
  float t = clamp(vT, 0.0, 1.0);
  float along = mix(1.5, 1.1, t);
  float land = 1.0 - smoothstep(0.96, 1.0, t);
  float motes = 0.72 + 0.28 * vnoise(vec2(vAng * 5.0, t * 26.0 - uTime * 1.4));
  gl_FragColor = vec4(uLight * core * along * land * motes * uGain, 1.0);
}
`;

const GLOW_VS = /* glsl */ `
uniform vec2 uSize;
varying vec2 vUv;
void main() {
  // Camera-facing sprite around the object's origin.
  vec3 center = (modelMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
  vec3 right = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
  vec3 up = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
  vec3 w = center + right * position.x * uSize.x + up * position.y * uSize.y;
  vUv = uv;
  gl_Position = projectionMatrix * viewMatrix * vec4(w, 1.0);
}
`;

const GLOW_FS = /* glsl */ `
uniform vec3 uLight;
uniform float uStrength;
varying vec2 vUv;
void main() {
  vec2 p = vUv * 2.0 - 1.0;
  float r2 = dot(p, p);
  float g = exp(-r2 * 4.5) + exp(-r2 * 28.0) * 1.4;
  gl_FragColor = vec4(uLight * g * uStrength * (1.0 - smoothstep(0.85, 1.0, r2)), 1.0);
}
`;

/** A soft additive glow sprite that always faces the camera (set uSize / uStrength). */
export function createGlowMaterial(color: string): ShaderMaterial {
  return new ShaderMaterial({
    name: 'glow',
    uniforms: { uLight: col(color), uSize: { value: new Vector2(1, 1) }, uStrength: { value: 1 } },
    vertexShader: GLOW_VS,
    fragmentShader: GLOW_FS,
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    side: DoubleSide,
  });
}

export interface PeakMaterials {
  rock: ShaderMaterial;
  fall: ShaderMaterial;
  halo: ShaderMaterial;
  /** Soft additive sprite (tip glow, the pool where the light lands); set uSize / uStrength. */
  glow: () => ShaderMaterial;
}

/** The inverted peak: its stone, the light falling from its tip, and glow sprites. */
export function createPeakMaterials(shared: SharedUniforms, map: MapDef): PeakMaterials {
  const b = map.atmosphere.beacon;
  const rock = new ShaderMaterial({
    name: 'peak',
    uniforms: { ...shared.all(), uRock: col(b.color), uLight: col(b.beam), uHaze: { value: b.haze * 0.88 } },
    vertexShader: PEAK_VS,
    fragmentShader: PEAK_FS,
  });
  const additive = { transparent: true, depthWrite: false, blending: AdditiveBlending, side: DoubleSide } as const;
  const fall = new ShaderMaterial({ name: 'peak-fall', uniforms: { ...shared.all(), uLight: col(b.beam), uGain: { value: 3.2 }, uSharp: { value: 2.6 } }, vertexShader: FALL_VS, fragmentShader: FALL_FS, ...additive });
  const glow = (): ShaderMaterial => createGlowMaterial(b.beam);
  // A faint, parallel glow around the beam so it reads against a bright sky.
  const halo = fall.clone();
  halo.uniforms = { ...shared.all(), uLight: col(b.beam), uGain: { value: 0.3 }, uSharp: { value: 1.2 } };
  return { rock, fall, halo, glow };
}

export interface DoorwayMaterials {
  stone: ShaderMaterial;
  light: ShaderMaterial;
  spill: ShaderMaterial;
}

/** The far-north doorway: weathered stone, the light in its opening, and the light it spills. */
export function createDoorwayMaterials(shared: SharedUniforms, map: MapDef, open: { width: number; height: number }): DoorwayMaterials {
  const b = map.atmosphere.beacon;
  const stone = new ShaderMaterial({
    name: 'doorway',
    uniforms: { ...shared.all(), uRock: col(b.color), uLight: col(b.beam), uOpen: { value: new Vector2(open.width, open.height) }, uHaze: { value: b.haze * 0.75 } },
    vertexShader: DOOR_VS,
    fragmentShader: DOOR_FS,
  });
  const light = new ShaderMaterial({
    name: 'doorway-light',
    uniforms: { ...shared.all(), uLight: col(b.beam) },
    vertexShader: DOOR_LIGHT_VS,
    fragmentShader: DOOR_LIGHT_FS,
  });
  const spill = new ShaderMaterial({
    name: 'doorway-spill',
    uniforms: { ...shared.all(), uLight: col(b.beam) },
    vertexShader: SPILL_VS,
    fragmentShader: SPILL_FS,
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    side: DoubleSide,
  });
  return { stone, light, spill };
}

/**
 * A tall, thin additive beam of light — the original far-north beacon beam. Marks a friend's
 * pearl in a shared run, and (as a signal from the ground) the far-north landmark. `root` is the
 * fraction of its height over which the base fades in: small = planted on the ground.
 */
export function createBeamMaterial(shared: SharedUniforms, color: string, root = 0.015): ShaderMaterial {
  return new ShaderMaterial({
    name: 'beam',
    uniforms: { ...shared.all(), uBeam: col(color), uOpacity: { value: 1 }, uRoot: { value: root } },
    vertexShader: BEAM_VS,
    fragmentShader: BEAM_FS,
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    side: DoubleSide,
  });
}

// ------------------------------------------------------------------------------------------ horizon

const HORIZON_VS = /* glsl */ `
attribute float aRow;
varying vec3 vWorldPos;
varying vec3 vNormal;
varying float vRow;
void main() {
  vec4 wPos = modelMatrix * vec4(position, 1.0);
  vWorldPos = wPos.xyz;
  vNormal = normalize(mat3(modelMatrix) * normal);
  vRow = aRow;
  gl_Position = projectionMatrix * viewMatrix * wPos;
}
`;

const HORIZON_FS = /* glsl */ `
${WORLD_UNIFORMS}
${NOISE}
${ATMOSPHERE}
${LIGHTING}
uniform vec3 uColor;
uniform vec3 uShade;
uniform float uKind;   // 0 clouds, 1 dunes
uniform float uHaze;
varying vec3 vWorldPos;
varying vec3 vNormal;
varying float vRow;
void main() {
  vec3 n = normalize(vNormal);
  vec3 V = normalize(cameraPosition - vWorldPos);
  vec3 rd = -V;
  vec3 c;
  if (uKind < 0.5) {
    // Painted cumulus: bright sunlit crowns, cool shaded bellies, a warm rim toward the sun.
    float sunSide = dot(normalize(vec3(rd.x, 0.0, rd.z)), normalize(vec3(uSunDir.x, 0.0, uSunDir.z)));
    float lit = smoothstep(0.0, 1.0, vRow) * 0.65 + smoothstep(-0.6, 1.0, sunSide) * 0.35;
    c = mix(uShade, uColor, lit) * (uSunColor * 0.32 + hemiAmbient(vec3(0.0, 1.0, 0.0)) * 0.85);
    c += uSunGlow * pow(max(sunSide, 0.0), 4.0) * smoothstep(0.55, 1.0, vRow) * 0.35;
  } else {
    float ndl = dot(n, uSunDir);
    c = mix(uShade, uColor, smoothstep(-0.35, 0.55, ndl)) * (uSunColor * 0.3 + hemiAmbient(n) * 0.8);
  }
  // Far away: mostly haze, densest at the base.
  float haze = mix(1.0, uHaze, smoothstep(0.0, 0.55, vRow));
  gl_FragColor = vec4(mix(c, fogColorFor(rd), haze), 1.0);
}
`;

export function createHorizonMaterial(shared: SharedUniforms, map: MapDef, haze: number): ShaderMaterial {
  const h = map.atmosphere.horizon;
  return new ShaderMaterial({
    name: 'horizon',
    uniforms: { ...shared.all(), uColor: col(h.color), uShade: col(h.shade), uKind: { value: h.kind === 'clouds' ? 0 : 1 }, uHaze: { value: haze } },
    vertexShader: HORIZON_VS,
    fragmentShader: HORIZON_FS,
  });
}
